import Routing from './routing.js';
import TIMLParser from './timl_parser.js';

export default class TIMLRenderer {
  static createRenderContext(overrides = {}, parentContext = globalThis) {
    const ctx = Object.create(parentContext);
    Object.entries(overrides).forEach(([key, value]) => {
      Object.defineProperty(ctx, key, {
        value,
        writable: true,
        configurable: true,
        enumerable: true
      });
    });
    return ctx;
  }

  static renderView(view, childView = '', renderContext = this.createRenderContext()) {
    if (!view) return document.createDocumentFragment();

    const ast = this.getAstForView(view);
    return this.renderAst(ast, view, childView, renderContext);
  }

  static renderComponent(componentView, props = {}, renderContext = this.createRenderContext()) {
    if (typeof componentView?.render === 'function') {
      const rendered = componentView.render(props, renderContext, this);
      if (rendered?.nodeType === 11) return rendered;
      if (rendered?.nodeType != null) {
        const fragment = document.createDocumentFragment();
        fragment.appendChild(rendered);
        return fragment;
      }
      if (typeof rendered === 'string') {
        const template = document.createElement('template');
        template.innerHTML = rendered.trim();
        this.normalizeClassnameAttributes(template.content);
        return template.content.cloneNode(true);
      }
      return document.createDocumentFragment();
    }

    const childContext = this.createRenderContext({ Props: props, ...props }, renderContext);
    return this.renderView(componentView, '', childContext);
  }

  static renderInto(target, view, props = {}, renderContext = this.createRenderContext()) {
    const fragment = this.renderComponent(view, props, renderContext);
    target.replaceChildren(fragment);
    return target;
  }

  static renderAst(ast, parentView, childView, renderContext) {
    const fragment = document.createDocumentFragment();

    for (const node of ast.children) {
      fragment.appendChild(this.renderNode(node, parentView, childView, renderContext));
    }

    return fragment;
  }

  static renderNode(node, parentView, childView, renderContext) {
    if (node.type === 'raw_html') {
      return this.renderRawHtmlNode(node, renderContext);
    }

    if (node.type === 'component') {
      return this.renderComponentNode(node, parentView, childView, renderContext);
    }

    if (node.type === 'element') {
      return this.renderElementNode(node, parentView, childView, renderContext);
    }

    throw new Error(`Unsupported TIML node type "${node.type}".`);
  }

  static renderComponentNode(node, parentView, childView, renderContext) {
    const componentView = this.getComponentView(parentView, node.name);
    if (!componentView) {
      const available = this.getAvailableComponents(parentView);
      const suggestion = this.findClosestMatch(node.name, available);
      const suffixParts = [];
      if (suggestion) suffixParts.push(`Did you mean "${suggestion}"?`);
      if (available.length) suffixParts.push(`Available components: ${available.join(', ')}`);
      const suffix = suffixParts.length ? ` ${suffixParts.join(' ')}` : '';
      throw new Error(`Unknown TIML component "${node.name}" at line ${node.lineNumber}.${suffix}`);
    }

    const props = {};
    const declaredProps = componentView.props || [];
    declaredProps.forEach(prop => {
      props[prop] = this.resolvePropValue(node.attrs[prop], renderContext);
    });

    return this.renderComponent(componentView, props, renderContext);
  }

  static renderElementNode(node, parentView, childView, renderContext) {
    const element = document.createElement(node.tag);
    const booleanAttrs = new Set(['required', 'checked', 'selected', 'disabled', 'readonly', 'multiple']);

    if (node.id) element.id = node.id;
    if (node.classes.length) element.className = node.classes.join(' ');
    if (node.text) element.textContent = this.replaceMustaches(node.text, renderContext).trim();

    for (const [name, rawValue] of Object.entries(node.attrs)) {
      let value = this.replaceMustaches(rawValue, renderContext);
      if (booleanAttrs.has(name)) {
        if (rawValue === '') {
          element.setAttribute(name, '');
          continue;
        }

        const normalized = String(value).trim().toLowerCase();
        if (!normalized || normalized === 'false' || normalized === '0' || normalized === 'null' || normalized === 'undefined') {
          continue;
        }

        element.setAttribute(name, '');
        continue;
      }
      if (name === 'href' && value.startsWith('/')) {
        value = `/${Routing.getLanguageCodeFromPathName()}${value}`;
      }
      element.setAttribute(name, value);
    }

    const componentName = element.getAttribute('component');
    if (componentName) {
      this.renderComponentIntoElement(element, componentName, parentView, childView, renderContext, node.lineNumber);
    }

    for (const childNode of node.children) {
      element.appendChild(this.renderNode(childNode, parentView, childView, renderContext));
    }

    return element;
  }

  static renderComponentIntoElement(element, componentName, parentView, childView, renderContext, lineNumber) {
    if (componentName === '?') {
      element.appendChild(this.renderView(childView, '', renderContext));
      return;
    }

    const componentView = this.getComponentView(parentView, componentName);
    if (!componentView) {
      const available = this.getAvailableComponents(parentView);
      const suggestion = this.findClosestMatch(componentName, available);
      const suffixParts = [];
      if (suggestion) suffixParts.push(`Did you mean "${suggestion}"?`);
      if (available.length) suffixParts.push(`Available components: ${available.join(', ')}`);
      const suffix = suffixParts.length ? ` ${suffixParts.join(' ')}` : '';
      throw new Error(`Unknown TIML component "${componentName}" at line ${lineNumber}.${suffix}`);
    }

    const props = {};
    const declaredProps = componentView.props || [];
    declaredProps.forEach(prop => {
      props[prop] = this.resolvePropValue(element.getAttribute(prop), renderContext);
      element.removeAttribute(prop);
    });

    element.appendChild(this.renderComponent(componentView, props, renderContext));
  }

  static getComponentView(parentView, componentName) {
    if (!parentView) return null;
    if (parentView.timlComponents?.[componentName]) return parentView.timlComponents[componentName];
    if (parentView[componentName]) return parentView[componentName];
    return null;
  }

  static getAvailableComponents(parentView) {
    if (!parentView) return [];
    const fromMap = Object.keys(parentView.timlComponents || {}).filter(
      key => parentView.timlComponents[key] && (
        typeof (parentView.timlComponents[key].timl ?? parentView.timlComponents[key].html) === 'string' ||
        typeof parentView.timlComponents[key].render === 'function'
      )
    );
    const fromStatics = Object.keys(parentView).filter(
      key => parentView[key] && (
        typeof (parentView[key].timl ?? parentView[key].html) === 'string' ||
        typeof parentView[key].render === 'function'
      )
    );
    return [...new Set([...fromMap, ...fromStatics])].sort();
  }

  static findClosestMatch(value, options) {
    if (!options.length) return null;
    let best = null;
    let bestDistance = Infinity;

    for (const option of options) {
      const distance = this.levenshteinDistance(value, option);
      if (distance < bestDistance) {
        best = option;
        bestDistance = distance;
      }
    }

    return bestDistance <= 3 ? best : null;
  }

  static levenshteinDistance(a, b) {
    const rows = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
    for (let i = 0; i <= a.length; i++) rows[i][0] = i;
    for (let j = 0; j <= b.length; j++) rows[0][j] = j;

    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        rows[i][j] = Math.min(
          rows[i - 1][j] + 1,
          rows[i][j - 1] + 1,
          rows[i - 1][j - 1] + cost
        );
      }
    }

    return rows[a.length][b.length];
  }

  static renderRawHtmlNode(node, renderContext) {
    const template = document.createElement('template');
    template.innerHTML = this.replaceMustaches(node.html, renderContext).trim();
    this.normalizeClassnameAttributes(template.content);
    return template.content.cloneNode(true);
  }

  static normalizeClassnameAttributes(root) {
    const nodes = [];
    if (root.childNodes) {
      root.childNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE && node.hasAttribute('classname')) {
          nodes.push(node);
        }
      });
    }
    if (root.querySelectorAll) {
      nodes.push(...root.querySelectorAll('[classname]'));
    }
    nodes.forEach(node => {
      node.className = node.getAttribute('classname');
      node.removeAttribute('classname');
    });
  }

  static getAstForView(view) {
    const template = view.timl ?? view.html;
    if (!view.__timlAst || view.__timlSource !== template) {
      view.__timlAst = TIMLParser.parse(view);
      view.__timlSource = template;
    }
    return view.__timlAst;
  }

  static resolvePath(path, ctx) {
    if (!/^[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*$/.test(path)) return undefined;

    const forbid = ['__proto__', 'constructor', 'prototype'];
    const keys = path.split('.');
    let obj = ctx;

    for (const key of keys) {
      if (obj == null || forbid.includes(key)) return undefined;
      obj = obj[key];
    }

    return obj;
  }

  static resolveExpression(expr, ctx) {
    const trimmedExpr = expr.trim();
    const fnMatch = trimmedExpr.match(/^([A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*)\(\)$/);
    if (fnMatch) {
      const fnPath = fnMatch[1];
      const pathParts = fnPath.split('.');
      const fnName = pathParts.pop();
      const ownerPath = pathParts.join('.');
      const owner = ownerPath ? this.resolvePath(ownerPath, ctx) : ctx;
      const fn = owner?.[fnName];
      if (typeof fn !== 'function') return undefined;
      try {
        return fn.call(owner);
      } catch {
        return undefined;
      }
    }

    return this.resolvePath(trimmedExpr, ctx);
  }

  static replaceMustaches(template, ctx = globalThis) {
    return String(template).replace(/{{\s*([A-Za-z_$][\w.$]*(?:\(\))?)\s*}}/g, (_, expr) => {
      const value = this.resolveExpression(expr, ctx);
      return value == null ? '' : String(value);
    });
  }

  static resolvePropValue(rawValue, ctx = globalThis) {
    if (rawValue == null) return '';
    const directExprMatch = String(rawValue).match(/^{{\s*([A-Za-z_$][\w.$]*(?:\(\))?)\s*}}$/);
    if (directExprMatch) {
      return this.resolveExpression(directExprMatch[1], ctx);
    }
    return this.replaceMustaches(rawValue, ctx);
  }
}
